/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  SearchUsersFilter,
  SearchUsersFilter$Outbound,
  SearchUsersFilter$outboundSchema,
} from "./searchusersfilter.js";

/**
 * Grouping dimension for results
 */
export const SearchUsersPayloadGroupBy = {
  Employee: "employee",
  Role: "role",
} as const;
/**
 * Grouping dimension for results
 */
export type SearchUsersPayloadGroupBy = ClosedEnum<
  typeof SearchUsersPayloadGroupBy
>;

/**
 * Level of usage metrics to compute per user. 'full' (default) returns the complete set: chat counts, cost, cache tokens, tool-call totals, and the per-tool and per-hook-source breakdowns. 'basic' computes only user identity, first/last activity, and input/output token sums — a much cheaper aggregation for large orgs (e.g. the employee enrollment list, which renders only those fields). The remaining fields are zero/empty under 'basic'. Ignored when source='agent_metrics'.
 */
export const Metrics = {
  Full: "full",
  Basic: "basic",
} as const;
/**
 * Level of usage metrics to compute per user. 'full' (default) returns the complete set: chat counts, cost, cache tokens, tool-call totals, and the per-tool and per-hook-source breakdowns. 'basic' computes only user identity, first/last activity, and input/output token sums — a much cheaper aggregation for large orgs (e.g. the employee enrollment list, which renders only those fields). The remaining fields are zero/empty under 'basic'. Ignored when source='agent_metrics'.
 */
export type Metrics = ClosedEnum<typeof Metrics>;

/**
 * Sort order
 */
export const SearchUsersPayloadSort = {
  Asc: "asc",
  Desc: "desc",
} as const;
/**
 * Sort order
 */
export type SearchUsersPayloadSort = ClosedEnum<typeof SearchUsersPayloadSort>;

/**
 * Where per-user summaries are read from (internal employee grouping only). 'logs' (default) scans raw telemetry_logs and computes the metrics selected by 'metrics'. 'agent_metrics' reads the pre-aggregated attribute_metrics_summaries view — canonical observed agent usage (Claude Code, Codex, Cursor, Claude Chat, LiteLLM), keyed by email — which is far cheaper but returns only identity, last activity (hourly), and input/output/total token sums; users without an email in the window are surfaced separately from raw logs with activity but no token counts.
 */
export const Source = {
  Logs: "logs",
  AgentMetrics: "agent_metrics",
} as const;
/**
 * Where per-user summaries are read from (internal employee grouping only). 'logs' (default) scans raw telemetry_logs and computes the metrics selected by 'metrics'. 'agent_metrics' reads the pre-aggregated attribute_metrics_summaries view — canonical observed agent usage (Claude Code, Codex, Cursor, Claude Chat, LiteLLM), keyed by email — which is far cheaper but returns only identity, last activity (hourly), and input/output/total token sums; users without an email in the window are surfaced separately from raw logs with activity but no token counts.
 */
export type Source = ClosedEnum<typeof Source>;

/**
 * Type of user identifier to group by
 */
export const SearchUsersPayloadUserType = {
  Internal: "internal",
  External: "external",
} as const;
/**
 * Type of user identifier to group by
 */
export type SearchUsersPayloadUserType = ClosedEnum<
  typeof SearchUsersPayloadUserType
>;

/**
 * Payload for searching user usage summaries
 */
export type SearchUsersPayload = {
  /**
   * Cursor for pagination (user identifier from last item)
   */
  cursor?: string | undefined;
  /**
   * Filter criteria for searching user usage summaries
   */
  filter: SearchUsersFilter;
  /**
   * Grouping dimension for results
   */
  groupBy?: SearchUsersPayloadGroupBy | undefined;
  /**
   * Number of items to return (1-1000)
   */
  limit?: number | undefined;
  /**
   * Level of usage metrics to compute per user. 'full' (default) returns the complete set: chat counts, cost, cache tokens, tool-call totals, and the per-tool and per-hook-source breakdowns. 'basic' computes only user identity, first/last activity, and input/output token sums — a much cheaper aggregation for large orgs (e.g. the employee enrollment list, which renders only those fields). The remaining fields are zero/empty under 'basic'. Ignored when source='agent_metrics'.
   */
  metrics?: Metrics | undefined;
  /**
   * Sort order
   */
  sort?: SearchUsersPayloadSort | undefined;
  /**
   * Where per-user summaries are read from (internal employee grouping only). 'logs' (default) scans raw telemetry_logs and computes the metrics selected by 'metrics'. 'agent_metrics' reads the pre-aggregated attribute_metrics_summaries view — canonical observed agent usage (Claude Code, Codex, Cursor, Claude Chat, LiteLLM), keyed by email — which is far cheaper but returns only identity, last activity (hourly), and input/output/total token sums; users without an email in the window are surfaced separately from raw logs with activity but no token counts.
   */
  source?: Source | undefined;
  /**
   * Type of user identifier to group by
   */
  userType: SearchUsersPayloadUserType;
};

/** @internal */
export const SearchUsersPayloadGroupBy$outboundSchema: z.ZodMiniEnum<
  typeof SearchUsersPayloadGroupBy
> = z.enum(SearchUsersPayloadGroupBy);

/** @internal */
export const Metrics$outboundSchema: z.ZodMiniEnum<typeof Metrics> = z.enum(
  Metrics,
);

/** @internal */
export const SearchUsersPayloadSort$outboundSchema: z.ZodMiniEnum<
  typeof SearchUsersPayloadSort
> = z.enum(SearchUsersPayloadSort);

/** @internal */
export const Source$outboundSchema: z.ZodMiniEnum<typeof Source> = z.enum(
  Source,
);

/** @internal */
export const SearchUsersPayloadUserType$outboundSchema: z.ZodMiniEnum<
  typeof SearchUsersPayloadUserType
> = z.enum(SearchUsersPayloadUserType);

/** @internal */
export type SearchUsersPayload$Outbound = {
  cursor?: string | undefined;
  filter: SearchUsersFilter$Outbound;
  group_by: string;
  limit: number;
  metrics: string;
  sort: string;
  source: string;
  user_type: string;
};

/** @internal */
export const SearchUsersPayload$outboundSchema: z.ZodMiniType<
  SearchUsersPayload$Outbound,
  SearchUsersPayload
> = z.pipe(
  z.object({
    cursor: z.optional(z.string()),
    filter: SearchUsersFilter$outboundSchema,
    groupBy: z._default(SearchUsersPayloadGroupBy$outboundSchema, "employee"),
    limit: z._default(z.int(), 50),
    metrics: z._default(Metrics$outboundSchema, "full"),
    sort: z._default(SearchUsersPayloadSort$outboundSchema, "desc"),
    source: z._default(Source$outboundSchema, "logs"),
    userType: SearchUsersPayloadUserType$outboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      groupBy: "group_by",
      userType: "user_type",
    });
  }),
);

export function searchUsersPayloadToJSON(
  searchUsersPayload: SearchUsersPayload,
): string {
  return JSON.stringify(
    SearchUsersPayload$outboundSchema.parse(searchUsersPayload),
  );
}
